import { useCallback, useEffect, useRef, useState } from "react";
import { getInvoicePayments, recordPayment } from "../../lib/api";
import { formatPriceCents } from "../../lib/money";
import type { InvoicePaymentsSummary, InvoiceRecord } from "../jobs/types";
import { MoneyInput } from "../../components/MoneyInput";
import styles from "./InvoicePaymentsPanel.module.css";

export interface InvoicePaymentsPanelProps {
  invoice: InvoiceRecord;
  /** Called after a payment is recorded so the host can refresh the row. */
  onChanged?: () => void;
  /**
   * Puts the cursor in the amount field on mount. The register's "Record
   * payment" action opens the row for this one purpose, so it lands the user
   * where they were going; the job card mounts the panel as one part of a
   * dialog and leaves focus alone.
   */
  autoFocusAmount?: boolean;
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Payment history + record-payment panel for a sent/paid invoice
 * (invoice-payments epic, US-PAY-003). Guard rules live server-side; this
 * panel reflects what the API returns and defaults the amount to the balance.
 */
export function InvoicePaymentsPanel({
  invoice,
  onChanged,
  autoFocusAmount = false,
}: InvoicePaymentsPanelProps) {
  const [summary, setSummary] = useState<InvoicePaymentsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [amountCents, setAmountCents] = useState(0);
  const [paidOn, setPaidOn] = useState(today);
  const [method, setMethod] = useState("");
  const [reference, setReference] = useState("");
  const amountFieldRef = useRef<HTMLLabelElement | null>(null);
  const focusedOnce = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      const body = await getInvoicePayments(invoice.id);
      setSummary(body);
      setAmountCents(body.balanceCents);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load payments");
    }
  }, [invoice.id]);

  useEffect(() => {
    void load();
  }, [load]);

  // The form only exists once the summary has loaded, so the focus has to wait
  // for it — and must happen once, not on every refresh after a payment.
  useEffect(() => {
    if (!autoFocusAmount || focusedOnce.current || summary === null) return;
    const input = amountFieldRef.current?.querySelector("input");
    if (input) {
      focusedOnce.current = true;
      input.focus();
      input.select();
    }
  }, [autoFocusAmount, summary]);

  const handleRecord = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await recordPayment(invoice.id, { amountCents, paidOn, method, reference });
      await load();
      onChanged?.();
    } catch (recordError) {
      setError(recordError instanceof Error ? recordError.message : "Could not record the payment");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={styles.root}>
      {summary === null ? (
        <p className={styles.note}>Loading…</p>
      ) : (
        <>
          <dl className={styles.summary}>
            <div className={styles.summaryRow}>
              <dt className={styles.fieldLabel}>Total</dt>
              <dd className={styles.value}>{formatPriceCents(summary.totalCents)}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt className={styles.fieldLabel}>Paid</dt>
              <dd className={styles.value}>{formatPriceCents(summary.paidCents)}</dd>
            </div>
            <div className={styles.summaryRow}>
              <dt className={styles.fieldLabel}>Balance</dt>
              <dd className={styles.balance}>{formatPriceCents(summary.balanceCents)}</dd>
            </div>
          </dl>

          {summary.payments.length > 0 && (
            <ul className={styles.list}>
              {summary.payments.map((payment) => (
                <li key={payment.id} className={styles.row}>
                  <span className={styles.rowDate}>{payment.paidOn}</span>
                  <span className={styles.rowMeta}>
                    {[payment.method, payment.reference].filter(Boolean).join(" · ") || "—"}
                  </span>
                  <span className={styles.rowAmount}>{formatPriceCents(payment.amountCents)}</span>
                </li>
              ))}
            </ul>
          )}

          {summary.balanceCents > 0 ? (
            <form className={styles.form} onSubmit={(event) => void handleRecord(event)}>
              <span className={styles.fieldLabel}>Record a payment</span>
              <div className={styles.formRow}>
                <label className={styles.field} ref={amountFieldRef}>
                  <span className={styles.fieldLabel}>Amount (AUD)</span>
                  <MoneyInput valueCents={amountCents} onChange={setAmountCents} name="paymentAmount" />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Date</span>
                  <input
                    className={styles.input}
                    type="date"
                    name="paymentDate"
                    value={paidOn}
                    onChange={(event) => setPaidOn(event.currentTarget.value)}
                  />
                </label>
              </div>
              <div className={styles.formRow}>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Method</span>
                  <input
                    className={styles.input}
                    name="paymentMethod"
                    value={method}
                    placeholder="Bank transfer, card, cash…"
                    onChange={(event) => setMethod(event.currentTarget.value)}
                  />
                </label>
                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Reference</span>
                  <input
                    className={styles.input}
                    name="paymentReference"
                    value={reference}
                    placeholder="Optional"
                    onChange={(event) => setReference(event.currentTarget.value)}
                  />
                </label>
              </div>
              <button type="submit" className={styles.record} disabled={busy}>
                {busy ? "Recording…" : "Record payment"}
              </button>
            </form>
          ) : (
            <p className={styles.note}>Paid in full{invoice.paidAt ? ` — ${invoice.paidAt.slice(0, 10)}` : ""}.</p>
          )}

          {error && (
            <p className={styles.error} role="alert">
              {error}
            </p>
          )}
        </>
      )}
    </div>
  );
}
